import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { cn } from '@/lib/utils';
import { Plus, RefreshCw, Search, Fish, Eye, Ban, RotateCcw } from 'lucide-react';
import {
    useSpeciesList,
    useSoftDeleteSpecies,
    useRestoreSpecies,
} from './hooks/useSpeciesAdmin';
import { SpeciesSubNav } from './components/SpeciesNav';
import type { SpeciesFilter } from './types';

const WATER_TYPE_OPTIONS: { value: SpeciesFilter['waterType']; label: string }[] = [
    { value: 'all', label: 'Todos' },
    { value: 'salada', label: 'Salada' },
    { value: 'dulce', label: 'Dulce' },
    { value: 'both', label: 'Ambos' },
];

const RARITY_OPTIONS: { value: SpeciesFilter['rarity']; label: string }[] = [
    { value: 'all', label: 'Todas' },
    { value: 'legendario', label: 'Maestría legendaria' },
    { value: 'épico', label: 'Habilidad Experto' },
    { value: 'raro', label: 'Destreza limitada' },
    { value: 'común', label: 'Casualidad' },
    { value: 'unknown', label: 'Desconocida' },
];

const STORAGE_BASE = 'https://aipecesprost.blob.core.windows.net/species-images/';

function normalizeForImage(name: string): string {
    return name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_.-]/g, '');
}

export function SpeciesPage() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [waterType, setWaterType] = useState<SpeciesFilter['waterType']>('all');
    const [rarity, setRarity] = useState<SpeciesFilter['rarity']>('all');
    const [includeInactive, setIncludeInactive] = useState(false);
    const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
    const [inAlbumOnly, setInAlbumOnly] = useState(false);
    const [missingDetails, setMissingDetails] = useState(false);
    const [missingImage, setMissingImage] = useState(false);
    const [deactivateSpeciesId, setDeactivateSpeciesId] = useState<number | null>(null);

    const { data: species, isLoading, refetch } = useSpeciesList({
        search: search || undefined,
        waterType: waterType === 'all' ? null : waterType,
        rarity: rarity === 'all' ? null : rarity,
        includeInactive,
        needsReviewOnly: needsReviewOnly || undefined,
        inAlbumOnly: inAlbumOnly || undefined,
        missingDetails: missingDetails || undefined,
        missingImage: missingImage || undefined,
    });
    const deleteMutation = useSoftDeleteSpecies();
    const restoreMutation = useRestoreSpecies();

    const filteredCount = species?.length ?? 0;

    const handleDelete = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setDeactivateSpeciesId(id);
    };

    const handleRestore = async (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        await restoreMutation.mutateAsync(id);
        refetch();
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Biblioteca de Especies"
                description="Catálogo completo, fichas editoriales, álbum Cromos y relaciones"
            >
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate('/species/new')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                    >
                        <Plus size={14} />
                        Nueva Especie
                    </button>
                    <button
                        onClick={() => refetch()}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                    >
                        <RefreshCw size={14} />
                        Actualizar
                    </button>
                </div>
            </PageHeader>

            <SpeciesSubNav />

            {/* Búsqueda + filtros */}
            <div className="space-y-3">
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar por nombre científico o común..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                    />
                </div>

                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3">
                        <div className="flex gap-1 p-1 bg-muted/50 rounded-lg flex-wrap">
                            {WATER_TYPE_OPTIONS.map(t => (
                                <button
                                    key={t.value}
                                    onClick={() => setWaterType(t.value)}
                                    className={cn(
                                        'px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap',
                                        waterType === t.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-1 p-1 bg-muted/50 rounded-lg flex-wrap">
                            {RARITY_OPTIONS.map(r => (
                                <button
                                    key={r.value}
                                    onClick={() => setRarity(r.value)}
                                    className={cn(
                                        'px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap',
                                        rarity === r.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeInactive}
                                onChange={e => setIncludeInactive(e.target.checked)}
                                className="rounded border-border"
                            />
                            Inactivas
                        </label>
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                            <input
                                type="checkbox"
                                checked={needsReviewOnly}
                                onChange={e => setNeedsReviewOnly(e.target.checked)}
                                className="rounded border-border"
                            />
                            Revisión
                        </label>
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                            <input
                                type="checkbox"
                                checked={inAlbumOnly}
                                onChange={e => setInAlbumOnly(e.target.checked)}
                                className="rounded border-border"
                            />
                            En álbum
                        </label>
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                            <input
                                type="checkbox"
                                checked={missingDetails}
                                onChange={e => setMissingDetails(e.target.checked)}
                                className="rounded border-border"
                            />
                            Sin ficha
                        </label>
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                            <input
                                type="checkbox"
                                checked={missingImage}
                                onChange={e => setMissingImage(e.target.checked)}
                                className="rounded border-border"
                            />
                            Sin imagen
                        </label>
                        <span className="text-xs text-muted-foreground shrink-0">
                            {filteredCount} especie{filteredCount !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>
            </div>

            {/* List */}
            {isLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="aspect-[3/4] rounded-xl border border-border bg-card animate-pulse" />
                    ))}
                </div>
            ) : !species || species.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
                    <Fish size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">No hay especies con los filtros seleccionados</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {species.map(s => {
                        const imgUrl = s.image_url || (s.image_filename ? `${STORAGE_BASE}${s.image_filename}` : null) ||
                            (s.scientific_name ? `${STORAGE_BASE}${normalizeForImage(s.scientific_name)}.png` : null);
                        const displayName = s.common_names_es || s.scientific_name || 'Sin nombre';
                        return (
                            <div
                                key={s.id}
                                className={cn(
                                    'group relative flex flex-col rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-lg transition-all text-left overflow-hidden',
                                    !s.is_active && 'opacity-60'
                                )}
                            >
                            <button
                                type="button"
                                onClick={() => navigate(`/species/${s.id}`)}
                                className="flex flex-col flex-1 text-left"
                            >
                                <div className="aspect-[4/5] bg-gradient-to-b from-muted/30 to-muted/10 relative overflow-hidden flex items-center justify-center">
                                    {imgUrl ? (
                                        <img src={imgUrl} alt={displayName} className="w-full h-full object-contain p-2" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Fish size={40} className="text-muted-foreground/40" />
                                        </div>
                                    )}
                                    {!s.is_active && (
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                            <span className="text-xs font-medium text-white bg-black/50 px-2 py-1 rounded">Inactiva</span>
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-2">
                                        <span className="text-xs text-white flex items-center gap-1">
                                            <Eye size={12} /> Ver detalle
                                        </span>
                                    </div>
                                </div>
                                <div className="p-2">
                                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                                    <p className="text-[10px] text-muted-foreground truncate">{s.scientific_name}</p>
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted">{s.water_type}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted">{s.rarity}</span>
                                        {s.is_in_album && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">Álbum</span>
                                        )}
                                        {s.in_cromos_album && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-700 dark:text-violet-300">Cromos</span>
                                        )}
                                        {!s.has_details && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300">Sin ficha</span>
                                        )}
                                        {s.needs_review && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300">Revisar</span>
                                        )}
                                    </div>
                                </div>
                            </button>
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {s.is_active ? (
                                    <button
                                        type="button"
                                        title="Desactivar especie"
                                        onClick={e => handleDelete(s.id, e)}
                                        className="p-1.5 rounded-md bg-background/90 border border-border text-muted-foreground hover:text-destructive"
                                    >
                                        <Ban size={12} />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        title="Reactivar especie"
                                        onClick={e => handleRestore(s.id, e)}
                                        className="p-1.5 rounded-md bg-background/90 border border-border text-muted-foreground hover:text-primary"
                                    >
                                        <RotateCcw size={12} />
                                    </button>
                                )}
                            </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <AdminConfirmDialog
                open={deactivateSpeciesId != null}
                variant="danger"
                title="Desactivar especie"
                description={
                    <p>No se eliminará; dejará de aparecer en la app hasta que la reactives.</p>
                }
                confirmLabel="Desactivar"
                onCancel={() => setDeactivateSpeciesId(null)}
                onConfirm={() => {
                    if (deactivateSpeciesId == null) return;
                    const id = deactivateSpeciesId;
                    setDeactivateSpeciesId(null);
                    deleteMutation.mutate(id, { onSuccess: () => void refetch() });
                }}
                confirmDisabled={deleteMutation.isPending}
            />
        </div>
    );
}
